import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { MdWarningAmber, MdRefresh } from 'react-icons/md';
import { cn } from '@/lib/utils';
import {
  CheckIcon,
  DatabaseIcon,
  DescriptionIcon,
  GenerateIcon,
  SearchIcon,
  TuneIcon,
} from './loadingStepIcons';

type IconType = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

type Props = {
  /** ローディング中かどうか */
  isLoading: boolean;
  /** 失敗状態かどうか */
  isFailed: boolean;
  /** プログレスメッセージ（任意） */
  progressMessage?: string | null;
  /** エラーメッセージ（任意） */
  errorMessage?: string | null;
  /** 再試行ハンドラ */
  onRetry: () => void;
  /** ローディング中のタイトル */
  loadingTitle?: string;
  /** 親要素いっぱいに広げるか */
  fillParent?: boolean;
};

type LoadingStep = {
  icon: IconType;
  title: string;
  subtitle?: string;
};

const LOADING_STEPS: LoadingStep[] = [
  {
    icon: DatabaseIcon,
    title: 'データベースから候補を抽出中',
  },
  {
    icon: TuneIcon,
    title: 'スキルセットとの適合性を分析',
  },
  {
    icon: SearchIcon,
    title: '経験年数と職種マッチング実行',
    subtitle: '優先度順に並び替え中',
  },
  {
    icon: DescriptionIcon,
    title: 'プロフィール情報を詳細解析',
    subtitle: '強みと希望条件を抽出',
  },
  {
    icon: GenerateIcon,
    title: '最適な求人候補を選定中',
  },
  {
    icon: CheckIcon,
    title: '求人を選定中',
    subtitle: 'マッチングを計算',
  },
];

// ステップ切替の合計サイクル時間（フェードアウト → スワップ → フェードインを含む）
const STEP_CYCLE_MS = 2400;
// フェードアウトの所要時間（このタイミングで内容を差し替えて、即フェードインに戻す）
const STEP_FADE_MS = 360;

const THINKING_DOT_DELAYS = [0, 0.22, 0.44] as const;

/** AI推薦実行中・エラー状態の表示パネル */
export default function RunStatusPanel({
  isLoading,
  isFailed,
  progressMessage,
  errorMessage,
  onRetry,
  loadingTitle = 'AI求人推薦を実行中',
  fillParent = false,
}: Props) {
  const [stepIndex, setStepIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setStepIndex(0);
      setIsFading(false);
      return;
    }
    // 完了APIの応答が来るまで最終ステップ手前で待機する（ループさせない）
    const lastWaitingIndex = LOADING_STEPS.length - 2;
    let timeoutId: number | undefined;
    const intervalId = window.setInterval(() => {
      // フェードアウト開始 → STEP_FADE_MS 後に index を進めてフェードインに戻す
      setIsFading(true);
      timeoutId = window.setTimeout(() => {
        setStepIndex((prev) => Math.min(prev + 1, lastWaitingIndex));
        setIsFading(false);
      }, STEP_FADE_MS);
    }, STEP_CYCLE_MS);
    return () => {
      window.clearInterval(intervalId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [isLoading]);

  if (isLoading) {
    const currentStep = LOADING_STEPS[stepIndex];
    const StepIcon = currentStep.icon;

    return (
      <div
        className={cn(
          'relative flex w-full flex-col items-center justify-center gap-sm overflow-hidden rounded-sm bg-gray p-xl',
          fillParent ? 'flex-1 self-stretch' : 'min-h-[337px]'
        )}
        role="status"
        aria-live="polite"
        aria-label="AI推薦実行中"
      >
        <div className="relative z-10 flex flex-col items-center gap-sm">
          <div className="flex size-[75px] animate-aiIconGlow items-center justify-center rounded-full bg-white">
            <span
              className={cn(
                'flex items-center justify-center transition-all duration-300 ease-out',
                isFading ? 'scale-[0.65] opacity-0' : 'scale-100 opacity-100'
              )}
            >
              <StepIcon size={48} className="text-primary" aria-hidden="true" />
            </span>
          </div>
          <div className="flex w-[280px] items-center justify-center px-[27px]">
            <p className="text-center text-sm leading-normal font-bold whitespace-nowrap text-black">
              {loadingTitle}
            </p>
          </div>
          <div
            className={cn(
              'flex min-h-[44px] flex-col items-center justify-center gap-xxs text-center transition-all duration-300 ease-out',
              isFading ? '-translate-y-1.5 opacity-0' : 'translate-y-0 opacity-100'
            )}
          >
            <p className="text-center text-xs leading-[1.7] whitespace-nowrap text-main">
              {progressMessage ?? currentStep.title}
            </p>
            {!progressMessage && currentStep.subtitle && (
              <p className="text-center text-xs leading-[1.7] whitespace-nowrap text-placeholder">
                {currentStep.subtitle}
              </p>
            )}
          </div>
          <div className="flex w-[280px] flex-col gap-xs">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/60">
              <div className="relative size-full overflow-hidden rounded-full bg-gradient-to-r from-blue-400 to-blue-800">
                <div className="absolute inset-0 animate-aiBarShimmer bg-aiBarShimmer" />
              </div>
            </div>
            <div className="flex w-full items-center justify-center gap-1.5">
              {LOADING_STEPS.map((step, i) => (
                <span
                  key={step.title}
                  className={cn(
                    'h-1.5 rounded-full transition-all duration-500 ease-out',
                    i === stepIndex ? 'w-6 bg-primary' : 'w-1.5 bg-gray-200'
                  )}
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>
          <div className="flex h-[16.5px] w-[243px] items-center gap-xs">
            <span className="flex h-1 w-4 items-center gap-0.5" aria-hidden="true">
              {THINKING_DOT_DELAYS.map((delay) => (
                <span
                  key={delay}
                  className="size-1 animate-aiThink rounded-full bg-primary"
                  style={{ animationDelay: `${delay}s` }}
                />
              ))}
            </span>
            <p className="min-w-0 flex-1 text-[11px] leading-normal tracking-[0.0645px] whitespace-nowrap text-placeholder">
              最適な候補を見つけるために分析しています
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isFailed) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-4 rounded-sm border border-solid border-red-300 bg-red-50 p-8"
        role="alert"
        aria-label="AI推薦エラー"
      >
        <MdWarningAmber size={32} className="text-red-600" aria-hidden="true" />
        <div className="text-center">
          <p className="text-sm font-medium text-red-800">AI推薦の実行に失敗しました</p>
          {errorMessage && <p className="mt-1 text-xs text-red-600">{errorMessage}</p>}
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="flex items-center gap-2 rounded border border-solid border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
        >
          <MdRefresh size={14} aria-hidden="true" />
          再試行
        </button>
      </div>
    );
  }

  return null;
}
